import type {TFunction} from 'i18next'
import {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Skeleton} from '@/components/ui/skeleton'
import {Hint} from '@/components/ui/hint'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {Tag} from '@/components/ui/tag'
import {DialogSubmitFooter} from '@/components/dialog-submit-footer'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {wompiMinimumProblem} from '@/components/cartera/amount-iva-fields'
import {
  DescuentoAtajo,
  ProcedenciaNote,
  PROCEDENCIA_LABEL_KEY,
  SegmentedToggle,
} from '@/components/cartera/price-form-controls'
import {
  CARTERA_PAGE_SIZE,
  RISK_GROUPS,
  SIZE_BANDS,
  useDeletePriceEntry,
  useResolverPrecio,
  useScopedPriceBook,
  useSetPriceOverride,
  type BillingCycle,
  type PriceBookEntry,
} from '@/hooks/inci/cartera/use-cartera'
import {translateApiError} from '@/lib/errors'
import {formatCopFull} from '@/lib/money'
import {formatRiskGroup, formatSegmentKey, formatSizeBandShort} from '@/lib/segments'
import {parseAmountCop, validateAmountCop} from '@/lib/validation'

type SizeBand = (typeof SIZE_BANDS)[number]
type RiskGroup = (typeof RISK_GROUPS)[number]
const CYCLES: BillingCycle[] = ['mensual', 'anual']

/** Una llave fuera de los ejes (p. ej. «51-200|I-III») cae al primer tramo para no dejar el
 * formulario en un estado imposible de enviar. */
function asSizeBand(raw: string | undefined): SizeBand {
  return SIZE_BANDS.find((band) => band === raw) ?? SIZE_BANDS[0]
}

function asRiskGroup(raw: string | undefined): RiskGroup {
  return RISK_GROUPS.find((group) => group === raw) ?? RISK_GROUPS[0]
}

type OverrideSeed = {size: SizeBand; risk: RiskGroup; cycle: BillingCycle; amount: string}

/** «Fijar» arranca en la banda de hoy sin quedar presa en ella; «Corregir», en la celda de esa fila. */
function seedForNew(segmentKey: string | null, cycle: BillingCycle): OverrideSeed {
  const [size, risk] = (segmentKey ?? '').split('|')
  return {size: asSizeBand(size), risk: asRiskGroup(risk), cycle, amount: ''}
}

function seedForEntry(entry: PriceBookEntry): OverrideSeed {
  const [size, risk] = (entry.segment_key ?? '').split('|')
  return {
    size: asSizeBand(size),
    risk: asRiskGroup(risk),
    cycle: entry.billing_cycle,
    amount: String(entry.amount_cop),
  }
}

function SegmentoElegido({segmentKey, currentSegmentKey}: {segmentKey: string; currentSegmentKey: string | null}) {
  const {t} = useTranslation()
  return (
    <div className="space-y-1" data-testid="empresa-saas-override-segmento-elegido">
      <p className="text-sm text-foreground">
        {t('cartera.empresaPanel.saasSegmentoElegido', {segment: formatSegmentKey(segmentKey, t)})}
      </p>
      {segmentKey === currentSegmentKey ? (
        <Tag tone="neutral">{t('cartera.empresaPanel.saasOverridesBandaActual')}</Tag>
      ) : (
        <p className="text-sm text-muted-foreground">
          {currentSegmentKey
            ? t('cartera.empresaPanel.saasSegmentoNoEsElActual', {
                segment: formatSegmentKey(currentSegmentKey, t),
              })
            : t('cartera.empresaPanel.saasSegmentoSinActual')}
        </p>
      )}
    </div>
  )
}

function OverrideDialog({
  seed,
  onClose,
  businessId,
  currentSegmentKey,
}: {
  seed: OverrideSeed | null
  onClose: () => void
  businessId: string
  currentSegmentKey: string | null
}) {
  const {t} = useTranslation()
  const setOverride = useSetPriceOverride()
  const [size, setSize] = useState<SizeBand>(SIZE_BANDS[0])
  const [risk, setRisk] = useState<RiskGroup>(RISK_GROUPS[0])
  const [cycle, setCycle] = useState<BillingCycle>('mensual')
  const [amount, setAmount] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!seed) return
    setSize(seed.size)
    setRisk(seed.risk)
    setCycle(seed.cycle)
    setAmount(seed.amount)
    setError(null)
  }, [seed])

  const segmentKey = `${size}|${risk}`
  const resolver = useResolverPrecio({segmentKey, billingCycle: cycle, businessId, enabled: seed !== null})

  function handleOpenChange(open: boolean) {
    if (!open) {
      setError(null)
      setOverride.reset()
      onClose()
    }
  }

  function submit() {
    const problem = translateAmountProblem(amount, t)
    if (problem) {
      setError(problem)
      return
    }
    setError(null)
    setOverride.mutate(
      {
        item_kind: 'saas',
        segment_key: segmentKey,
        billing_cycle: cycle,
        amount_cop: parseAmountCop(amount),
        business_id: businessId,
      },
      {onSuccess: () => handleOpenChange(false), onError: (err) => setError(translateApiError(err))}
    )
  }

  return (
    <Dialog open={seed !== null} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="empresa-saas-override-dialog">
        <DialogHeader>
          <DialogTitle>{t('cartera.empresaPanel.saasOverrideDialogTitle')}</DialogTitle>
        </DialogHeader>
        <form
          noValidate
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault()
            submit()
          }}>
          <div className="space-y-1.5">
            <Label>{t('cartera.empresaPanel.saasBandaLabel')}</Label>
            <SegmentedToggle
              value={size}
              options={SIZE_BANDS}
              optionLabel={(band) => formatSizeBandShort(band, t)}
              onChange={setSize}
              testId="empresa-saas-override-banda"
            />
          </div>

          <div className="space-y-1.5">
            <Label>{t('cartera.empresaPanel.saasRiesgoLabel')}</Label>
            <SegmentedToggle
              value={risk}
              options={RISK_GROUPS}
              optionLabel={(group) => formatRiskGroup(group, t)}
              onChange={setRisk}
              testId="empresa-saas-override-riesgo"
            />
          </div>

          <div className="space-y-1.5">
            <Label>{t('cartera.empresaPanel.saasCicloLabel')}</Label>
            <SegmentedToggle
              value={cycle}
              options={CYCLES}
              optionLabel={(option) => t(`cartera.precios.${option}`)}
              onChange={setCycle}
              testId="empresa-saas-override-ciclo"
            />
          </div>

          <SegmentoElegido segmentKey={segmentKey} currentSegmentKey={currentSegmentKey} />

          <ProcedenciaNote
            loading={resolver.isLoading}
            resolution={resolver.data}
            testId="empresa-saas-override-procedencia"
          />

          <DescuentoAtajo
            baseAmount={resolver.data?.amount_cop ?? null}
            onApply={(value) => setAmount(String(value))}
          />

          <div className="space-y-1.5">
            <Label htmlFor="empresa-saas-override-amount">{t('cartera.precios.amountLabel')}</Label>
            <Input
              // Excepción a NUM (10, para conteos): pesos con separadores ocupan 11 («120.000.000»).
              // 20 cubre cualquier precio sin admitir un párrafo.
              maxLength={20}
              id="empresa-saas-override-amount"
              data-testid="empresa-saas-override-amount"
              inputMode="numeric"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onBlur={() => setError(amount.trim() === '' ? null : translateAmountProblem(amount, t))}
            />
          </div>
          {error && (
            <p className="text-sm text-destructive" data-testid="empresa-saas-override-error">
              {error}
            </p>
          )}
          <DialogSubmitFooter
            onCancel={() => handleOpenChange(false)}
            onSubmit={submit}
            submitLabel={t('cartera.precios.submit')}
            pending={setOverride.isPending}
            testId="empresa-saas-override-submit"
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}

// El override creado recibe el IVA por defecto del backend (`PriceBookEntryCreate.iva_pct = 19`).
const IMPLICIT_IVA_PCT = 19

/** Un campo vacío no es error hasta enviar; incluye el piso de Wompi. */
function translateAmountProblem(amount: string, t: TFunction): string | null {
  const problem = validateAmountCop(amount)
  if (problem) return t(problem)
  return wompiMinimumProblem(t, Math.round(parseAmountCop(amount)), IMPLICIT_IVA_PCT)
}

function OverrideRow({
  entry,
  currentSegmentKey,
  billingCycle,
  onCorregir,
  onQuitar,
}: {
  entry: PriceBookEntry
  currentSegmentKey: string | null
  billingCycle: BillingCycle
  onCorregir: () => void
  onQuitar: () => void
}) {
  const {t} = useTranslation()
  const esBandaActual = entry.segment_key === currentSegmentKey
  const aplicaHoy = esBandaActual && entry.billing_cycle === billingCycle
  const nota = esBandaActual
    ? t('cartera.empresaPanel.saasOverridesCicloOtro')
    : t('cartera.empresaPanel.saasOverridesEspera')

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-2 border-b border-border py-2 last:border-0"
      data-testid={`empresa-saas-override-fila-${entry.segment_key}-${entry.billing_cycle}`}>
      <div className="space-y-0.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-foreground">{formatSegmentKey(entry.segment_key ?? '', t)}</span>
          <span className="text-sm text-muted-foreground">{t(`cartera.precios.${entry.billing_cycle}`)}</span>
          {aplicaHoy ? (
            <Tag tone="green">{t('cartera.empresaPanel.saasOverridesAplicaHoy')}</Tag>
          ) : (
            <Tag tone="neutral">{nota}</Tag>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="font-mono text-sm tabular-nums text-foreground">{formatCopFull(entry.amount_cop)}</span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          data-testid={`empresa-saas-override-corregir-${entry.id}`}
          onClick={onCorregir}>
          {t('cartera.empresaPanel.saasOverrideCorregir')}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="text-destructive hover:text-destructive"
          data-testid={`empresa-saas-override-quitar-${entry.id}`}
          onClick={onQuitar}>
          {t('cartera.empresaPanel.saasOverrideQuitarCorto')}
        </Button>
      </div>
    </div>
  )
}

function PrecioDeHoy({
  segmentKey,
  billingCycle,
  businessId,
}: {
  segmentKey: string | null
  billingCycle: BillingCycle
  businessId: string
}) {
  const {t} = useTranslation()
  const resolver = useResolverPrecio({segmentKey, billingCycle, businessId, enabled: !!segmentKey})
  const resolution = resolver.data

  if (!segmentKey)
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Tag tone="amber">{t('cartera.empresaPanel.saasSinBanda')}</Tag>
        <Hint>{t('cartera.empresaPanel.saasSinSegmento')}</Hint>
      </div>
    )
  if (resolver.isLoading) return <Skeleton className="h-5 w-40" />
  if (!resolution) return <p className="text-sm text-muted-foreground">{t('cartera.overrides.sinPrecioVigente')}</p>
  return (
    <p className="text-sm text-foreground">
      {resolution.procedencia === 'tenant'
        ? t('cartera.empresaPanel.saasPagaOverride', {amount: formatCopFull(resolution.amount_cop)})
        : t('cartera.empresaPanel.saasPagaBase', {
            amount: formatCopFull(resolution.amount_cop),
            source: t(PROCEDENCIA_LABEL_KEY[resolution.procedencia] ?? 'cartera.overrides.fuenteBaseGlobal'),
          })}
    </p>
  )
}

/** Corregir es el mismo POST sobre la celda (el backend supersede, nunca edita) y quitar supersede
 * sin reemplazo. Una empresa sin banda resuelta también puede registrar overrides. */
export function EmpresaPrecioSaas({
  businessId,
  segmentKey,
  billingCycle,
}: {
  businessId: string
  segmentKey: string | null
  billingCycle: BillingCycle
}) {
  const {t} = useTranslation()
  const [seed, setSeed] = useState<OverrideSeed | null>(null)
  const [removing, setRemoving] = useState<PriceBookEntry | null>(null)
  const deleteEntry = useDeletePriceEntry()
  const [page, setPage] = useState(1)
  // Vigencia, tipo y orden los decide el servidor (R6): filtrar una página ya cortada deja
  // páginas a medio llenar y un total falso.
  const scoped = useScopedPriceBook({businessId}, page, 'saas')
  const overrides = scoped.data?.items ?? []

  return (
    <div className="space-y-4" data-testid="empresa-precio-saas">
      <div className="flex flex-wrap items-center gap-2">
        <PrecioDeHoy segmentKey={segmentKey} billingCycle={billingCycle} businessId={businessId} />
        <Button
          type="button"
          size="sm"
          variant="outline"
          data-testid="empresa-saas-override-editar"
          onClick={() => setSeed(seedForNew(segmentKey, billingCycle))}>
          {t('cartera.empresaPanel.saasOverrideNuevo')}
        </Button>
      </div>

      <div className="space-y-1">
        <h3 className="font-mono text-[11px] font-medium tracking-wider text-muted-foreground uppercase">
          {t('cartera.empresaPanel.saasOverridesTitulo')}
        </h3>
        {scoped.isLoading && !scoped.data ? (
          <Skeleton className="h-16 w-full" />
        ) : overrides.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="empresa-saas-overrides-vacio">
            {t('cartera.empresaPanel.saasOverridesVacio')}
          </p>
        ) : (
          <div data-testid="empresa-saas-overrides-lista">
            {overrides.map((entry) => (
              <OverrideRow
                key={entry.id}
                entry={entry}
                currentSegmentKey={segmentKey}
                billingCycle={billingCycle}
                onCorregir={() => setSeed(seedForEntry(entry))}
                onQuitar={() => setRemoving(entry)}
              />
            ))}
          </div>
        )}
        {!!scoped.data?.total && (
          <PaginationBar page={page} pageSize={CARTERA_PAGE_SIZE} total={scoped.data.total} onPageChange={setPage} />
        )}
      </div>

      <OverrideDialog
        seed={seed}
        onClose={() => setSeed(null)}
        businessId={businessId}
        currentSegmentKey={segmentKey}
      />
      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title={t('cartera.empresaPanel.saasOverrideQuitarTitle')}
        description={t('cartera.empresaPanel.saasOverrideQuitarDescription')}
        confirmLabel={t('cartera.empresaPanel.saasOverrideQuitar')}
        cancelLabel={t('common.cancel')}
        pending={deleteEntry.isPending}
        onConfirm={() => {
          if (!removing) return
          deleteEntry.mutate(removing.id, {onSuccess: () => setRemoving(null)})
        }}
      />
    </div>
  )
}
